import Image from 'next/image'
import Link from 'next/link'
import type { StorefrontProduct } from '@sifly/shared-types'
import { cropSquareUrl } from '@sifly/shared-utils'
import { ImagePlaceholder } from './ImagePlaceholder'
import { CardPrice, SaleBadge } from './CardPrice'

export type ProductGridProps = {
  products: StorefrontProduct[]
  columns?: 2 | 3 | 4
}

const colClasses = {
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-2 lg:grid-cols-3',
  4: 'sm:grid-cols-2 lg:grid-cols-4',
} as const

export function ProductGrid({ products, columns = 3 }: ProductGridProps) {
  return (
    <div
      className={`grid grid-cols-1 ${colClasses[columns]} gap-6`}
      data-component="ProductGrid"
    >
      {products.map((product) => {
        // Square crop (`sq`), anchored on the photo's focal point when the
        // dealer set one, else content-aware. See cropSquareUrl.
        const cardImage = cropSquareUrl(product.photos[0])
        return (
        <Link
          key={product.id}
          href={`/products/${product.slug ?? product.id}`}
          className="group block rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-bg-primary)] overflow-hidden hover:shadow-[var(--shadow-md)] transition-shadow"
        >
          <div className="relative aspect-square bg-[var(--color-bg-secondary)]">
            {cardImage ? (
              <Image
                src={cardImage}
                alt={product.name}
                fill
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                className="object-cover group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <ImagePlaceholder label={product.name} className="absolute inset-0" />
            )}
            {product.onSale && <SaleBadge className="absolute left-2 top-2" />}
          </div>
          <div className="p-4">
            <h3
              className="text-sm font-medium text-[var(--color-text-primary)] line-clamp-2"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              {product.name}
            </h3>
            <CardPrice
              displayPrice={product.displayPrice}
              compareAtPrice={product.compareAtPrice}
              priceFromMultiple={product.priceFromMultiple}
              onSale={product.onSale}
              className="mt-1 text-sm text-[var(--color-text-secondary)] [font-family:var(--font-body)]"
            />
          </div>
        </Link>
        )
      })}
    </div>
  )
}
